import { Component } from 'react';
import { compose } from 'recompose';
import { ChartFragment, HomeMedicationRecord } from 'models/api-response';
import { FormFieldInputType } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps } from 'models/ui';
import { NAV_ID } from 'constants/app.constant';
import { appHelper, chartHelper } from 'helpers';
import { chartService } from 'services';
import { withChartLogic } from 'components/common';
import HomeMedicationView, { HomeMedicationViewProps } from 'components/features/chart/patient-data/home-medication/HomeMedicationView';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import { FormField, FormFieldLabel } from './constants';

interface HomeMedicationState {
  fragments: ChartFragment[];
  chartHistory: HomeMedicationRecord[];
}
export interface HomeMedicationProps extends ChartComponentProps {
  enableMultiStepsAuthoring: Function;
}

class HomeMedication extends Component<HomeMedicationProps, HomeMedicationState> {
  static displayName = 'HomeMedication';

  navIds: string[];

  constructor(props) {
    super(props);
    this.state = {
      fragments: [],
      chartHistory: []
    };
    this.navIds = [NAV_ID.HOME_MEDICATION];
  }

  componentDidMount() {
    this.loadHomeMedicationData();
  }

  loadHomeMedicationData = () => {
    this.props.initState(this.buildFormFields());
    this.props.enableMultiStepsAuthoring(true);
    const loadAdmissionHistoryHomeMedication = this.props.loadChartData();
    const loadPatientDataHomeMedication = this.props.loadChartData(undefined, this.navIds);
    appHelper.useLoader(Promise.all([loadAdmissionHistoryHomeMedication, loadPatientDataHomeMedication])).then(this.handleLoadChartData);
  };

  handleLoadChartData = (response) => {
    const [loadAdmissionHistoryHomeMedication, loadPatientDataHomeMedication] = response;
    const data = [...loadAdmissionHistoryHomeMedication.data, ...loadPatientDataHomeMedication.data];
    const records = data.map((fragment) => {
      const { fragmentId: id, active, chartingAt, creator, modifier } = fragment;
      return { id, active, chartingAt, creator, modifier, ...fragment.chartData };
    });
    this.setState({ fragments: data, chartHistory: records });
  };

  buildFormFields = () => {
    const { createFormField } = chartHelper;
    const dataMap = new Map();
    const multiselectRadios = [
      { name: FormField.HOME_MEDICATION_TYPE, label: FormFieldLabel.HOME_MEDICATION_TYPE },
      { name: FormField.MEDICATION, label: FormFieldLabel.MEDICATION },
      { name: FormField.DOSE, label: FormFieldLabel.DOSE },
      { name: FormField.FORM, label: FormFieldLabel.FORM },
      { name: FormField.ROUTE, label: FormFieldLabel.ROUTE },
      { name: FormField.FREQUENCY, label: FormFieldLabel.FREQUENCY },
      { name: FormField.BRAND_NAME, label: FormFieldLabel.BRAND_NAME },
      { name: FormField.GENERIC_NAME, label: FormFieldLabel.GENERIC_NAME },
      { name: FormField.PRODUCT_TYPE, label: FormFieldLabel.PRODUCT_TYPE },
      { name: FormField.PRODUCT, label: FormFieldLabel.PRODUCT },
      { name: FormField.PREPARATION, label: FormFieldLabel.PREPARATION },
      { name: FormField.STRENGTH, label: FormFieldLabel.STRENGTH },
      { name: FormField.OVER_THE_COUNTER_PRODUCT_FORM, label: FormFieldLabel.FORM },
      { name: FormField.HERBAL_AND_NATURAL_REMEDY_PRODUCTS_ROUTE, label: FormFieldLabel.ROUTE }
    ];
    const textBoxes = [
      { name: FormField.INDICATION, label: FormFieldLabel.INDICATION },
      { name: FormField.OVER_THE_COUNTER_PRODUCT_REASON_FOR_TAKING_PRODUCT, label: FormFieldLabel.REASON_FOR_TAKING_PRODUCT },
      { name: FormField.OVER_THE_COUNTER_PRODUCT_RESULT_FOLLOWING_USE, label: FormFieldLabel.RESULT_FOLLOWING_USE },
      { name: FormField.OVER_THE_COUNTER_PRODUCT_DOSE, label: FormFieldLabel.DOSE },
      { name: FormField.STRENGTH, label: FormFieldLabel.STRENGTH },
      { name: FormField.HERBAL_AND_NATURAL_REMEDY_PRODUCTS_REASON_FOR_TAKING_PRODUCT, label: FormFieldLabel.REASON_FOR_TAKING_PRODUCT },
      { name: FormField.HERBAL_AND_NATURAL_REMEDY_PRODUCTS_RESULT_FOLLOWING_USE, label: FormFieldLabel.RESULT_FOLLOWING_USE },
      { name: FormField.HERBAL_AND_NATURAL_REMEDY_PRODUCTS_DOSE, label: FormFieldLabel.DOSE },
      { name: FormField.INFORMATION_RECEIVED_FROM_ORDER, label: 'Other' },
      { name: FormField.SPECIAL_DOSE, label: FormFieldLabel.SPECIAL_DOSE },
      { name: FormField.DOSE_UNITS, label: FormFieldLabel.DOSE_UNITS }
    ];

    const radioChoice = [
      { name: FormField.OVER_THE_COUNTER_PRODUCT_TAKEN_IN_LAST_24HOURS, label: FormFieldLabel.TAKEN_IN_LAST_24HOURS },
      { name: FormField.HERBAL_AND_NATURAL_REMEDY_PRODUCTS_TAKEN_IN_LAST_24HOURS, label: FormFieldLabel.TAKEN_IN_LAST_24HOURS }
    ];

    dataMap.set(FormField.INFORMATION_RECEIVED_FROM, createFormField({ name: FormField.INFORMATION_RECEIVED_FROM, type: FormFieldInputType.MULTISELECT_DROPDOWN }));
    multiselectRadios.forEach(({ name, label }) => dataMap.set(name, createFormField({ name, type: FormFieldInputType.MULTI_SELECT_RADIO, label })));
    textBoxes.forEach(({ name, label }) => dataMap.set(name, createFormField({ name, type: FormFieldInputType.TEXT_BOX, label })));
    if (!this.props.isAuthor) {
      const timeTextBox = [{ name: FormField.LAST_DOSE_TIME, label: FormFieldLabel.LAST_DOSE_TIME }];
      const dateTextBox = [{ name: FormField.LAST_DOSE_DATE, label: FormFieldLabel.LAST_DOSE_DATE }];
      timeTextBox.forEach(({ name, label }) =>
        dataMap.set(
          name,
          createFormField({
            name,
            type: FormFieldInputType.TIME,
            label,
            value: '00:00'
          })
        )
      );
      dateTextBox.forEach(({ name, label }) =>
        dataMap.set(
          name,
          createFormField({
            name,
            type: FormFieldInputType.DATE,
            label
          })
        )
      );
    }
    if (this.props.isAuthor) {
      dataMap.set(
        FormField.MEDICATION_TIME_OFFSET,
        createFormField({
          name: FormField.MEDICATION_TIME_OFFSET,
          type: FormFieldInputType.TEXT_BOX,
          label: FormFieldLabel.MEDICATION_TIME_OFFSET,
          value: '00:00'
        })
      );
    }
    radioChoice.forEach(({ name, label }) => dataMap.set(name, createFormField({ name, type: FormFieldInputType.RADIO_CHOICE, label })));

    return dataMap;
  };

  handleCancelClick = () => this.props.handleDiscardClick(undefined, this.buildFormFields());

  handleSaveClick = () => {
    this.props.saveChart([this.buildFragment()], { defaultFormFields: this.buildFormFields(), afterSave: this.loadHomeMedicationData });
  };

  buildFragment = () => {
    const { formFieldMap } = this.props;
    const { buildRecordFromField } = chartService;

    const chartData = {};
    Object.keys(FormField).forEach((key) => {
      chartData[FormField[key]] = buildRecordFromField(formFieldMap.get(FormField[key]))?.content ?? '';
    });

    const basicInfo = chartService.createBaseFragment({ chartingTime: this.props.chartingTime });
    return { ...basicInfo, chartData } as ChartFragment;
  };

  deleteHistory = (record) => {
    const updateFragment = this.state.fragments.find((fragment) => fragment.fragmentId === record.id);
    appHelper.useLoader(
      this.props
        .saveChartData({ ...updateFragment, active: false }, updateFragment.navElementId)
        .then(this.loadHomeMedicationData)
        .then(this.props.showDeleteSuccess)
    );
  };

  render() {
    const { enableDisplayRecordsButton, hasUnsavedChanges, displayAuthoringData, formFieldMap, formSubmittedCount, locale } = this.props;
    const { chartHistory } = this.state;

    const chartActionsProps: ChartActionsComponentProps = {
      enableDisplayRecordsButton,
      enableSaveButton: hasUnsavedChanges,
      onCancelClick: this.handleCancelClick,
      onDisplayRecordsClick: displayAuthoringData,
      onSaveClick: this.handleSaveClick
    };
    const viewProps: HomeMedicationViewProps = {
      chartActionsProps,
      formFieldMap,
      formSubmittedCount,
      chartHistory,
      deleteHistory: this.deleteHistory,
      locale
    };
    return <HomeMedicationView {...viewProps} />;
  }
}

export { HomeMedication as BaseHomeMedication };
export default compose(withSavedPatientChartsPage, withChartLogic)(HomeMedication);
